// Cursor spotlight trail for the hero journey cascade (mirrors landing-brand-background.html).

import type { HeroJourneyCascadeFit } from '@/components/support-mainnet/hero-journey-cascade-engine';

export const HERO_JOURNEY_CASCADE_SPOTLIGHT = {
  radiusPx: 90,
  maxInfluence: 0.85,
  scaleBoost: 0.5,
  trailMax: 40,
  trailMinPx: 4,
  trailDecay: 0.97,
  trailCull: 0.02,
  trailFadeMs: 900,
  shineRgb: [0x80, 0x95, 0xff] as const,
} as const;

type SpotlightSample = {
  x: number;
  y: number;
  strength: number;
  fadeStartStrength: number;
};

export type HeroJourneyCascadeSpotlightTrail = {
  samples: SpotlightSample[];
  fading: boolean;
  fadeStartMs: number;
};

export function createHeroJourneyCascadeSpotlightTrail(): HeroJourneyCascadeSpotlightTrail {
  return { samples: [], fading: false, fadeStartMs: 0 };
}

export function hostLocalToCascadeArtboard(
  localX: number,
  localY: number,
  fit: HeroJourneyCascadeFit,
): { x: number; y: number } {
  return {
    x: (localX - fit.ox) / fit.scale,
    y: (localY - fit.oy) / fit.scale,
  };
}

function smoothstep01(t: number): number {
  return t * t * (3 - 2 * t);
}

function spotRadiusArtboard(fitScale: number): number {
  return HERO_JOURNEY_CASCADE_SPOTLIGHT.radiusPx / fitScale;
}

function spotMinDistArtboard(fitScale: number): number {
  return HERO_JOURNEY_CASCADE_SPOTLIGHT.trailMinPx / fitScale;
}

function spotlightSampleDistance01(
  x: number,
  y: number,
  sampleX: number,
  sampleY: number,
  sampleStrength: number,
  fitScale: number,
): number {
  if (sampleStrength <= 0) {
    return 0;
  }
  const dist = Math.hypot(x - sampleX, y - sampleY);
  const radius = spotRadiusArtboard(fitScale);
  if (dist >= radius) {
    return 0;
  }
  return (1 - dist / radius) * sampleStrength;
}

export function getHeroJourneyCascadeSpotlightInfluence(
  trail: HeroJourneyCascadeSpotlightTrail,
  artboardX: number,
  artboardY: number,
  fitScale: number,
): number {
  let maxDistance01 = 0;
  for (const sample of trail.samples) {
    const distance01 = spotlightSampleDistance01(
      artboardX,
      artboardY,
      sample.x,
      sample.y,
      sample.strength,
      fitScale,
    );
    if (distance01 > maxDistance01) {
      maxDistance01 = distance01;
    }
  }
  return maxDistance01;
}

function parseHexRgb(hex: string): [number, number, number] {
  const normalized = hex.replace('#', '');
  const value = Number.parseInt(normalized, 16);
  return [(value >> 16) & 0xff, (value >> 8) & 0xff, value & 0xff];
}

export function applyHeroJourneyCascadeSpotlight(
  colorHex: string,
  alpha: number,
  radius: number,
  slash: number,
  influenceDistance01: number,
): { color: string; alpha: number; radius: number; slash: number } {
  if (influenceDistance01 <= 0) {
    return { color: colorHex, alpha, radius, slash };
  }

  const eased = smoothstep01(Math.min(1, influenceDistance01));
  const colorFactor = eased * eased;
  const influence = colorFactor * HERO_JOURNEY_CASCADE_SPOTLIGHT.maxInfluence;
  if (influence <= 0.002) {
    return { color: colorHex, alpha, radius, slash };
  }

  const [cr, cg, cb] = parseHexRgb(colorHex);
  const [sr, sg, sb] = HERO_JOURNEY_CASCADE_SPOTLIGHT.shineRgb;
  const nr = Math.round(cr + (sr - cr) * influence);
  const ng = Math.round(cg + (sg - cg) * influence);
  const nb = Math.round(cb + (sb - cb) * influence);
  const na = alpha + (1 - alpha) * influence * 0.7;
  const scale = 1 + eased * HERO_JOURNEY_CASCADE_SPOTLIGHT.scaleBoost;

  return {
    color: `rgb(${nr},${ng},${nb})`,
    alpha: na > 1 ? 1 : na,
    radius: radius * scale,
    slash: slash * scale,
  };
}

function clearSpotlightTrail(trail: HeroJourneyCascadeSpotlightTrail): void {
  trail.samples = [];
  trail.fading = false;
}

function pushSpotlightTrailSample(
  trail: HeroJourneyCascadeSpotlightTrail,
  x: number,
  y: number,
  fitScale: number,
): void {
  const lastSample = trail.samples[trail.samples.length - 1];
  if (lastSample) {
    const dist = Math.hypot(lastSample.x - x, lastSample.y - y);
    if (dist < spotMinDistArtboard(fitScale) && lastSample.strength > 0.85) {
      lastSample.x = x;
      lastSample.y = y;
      lastSample.strength = 1;
      lastSample.fadeStartStrength = 1;
      return;
    }
  }
  trail.samples.push({ x, y, strength: 1, fadeStartStrength: 1 });
  if (trail.samples.length > HERO_JOURNEY_CASCADE_SPOTLIGHT.trailMax) {
    trail.samples.shift();
  }
}

export function setHeroJourneyCascadeSpotlightPosition(
  trail: HeroJourneyCascadeSpotlightTrail,
  position: { x: number; y: number } | null,
  nowMs: number,
  fitScale: number,
): void {
  if (position === null) {
    if (trail.samples.length === 0) {
      return;
    }
    trail.fading = true;
    trail.fadeStartMs = nowMs;
    for (const sample of trail.samples) {
      sample.fadeStartStrength = sample.strength;
    }
    return;
  }

  trail.fading = false;
  pushSpotlightTrailSample(trail, position.x, position.y, fitScale);
}

export function tickHeroJourneyCascadeSpotlightTrail(
  trail: HeroJourneyCascadeSpotlightTrail,
  nowMs: number,
): void {
  if (trail.fading) {
    const t = (nowMs - trail.fadeStartMs) / HERO_JOURNEY_CASCADE_SPOTLIGHT.trailFadeMs;
    if (t >= 1) {
      clearSpotlightTrail(trail);
      return;
    }
    const multiplier = 1 - t;
    for (const sample of trail.samples) {
      sample.strength = sample.fadeStartStrength * multiplier;
    }
    return;
  }

  for (let index = trail.samples.length - 1; index >= 0; index -= 1) {
    const sample = trail.samples[index];
    if (sample === undefined) {
      continue;
    }
    sample.strength *= HERO_JOURNEY_CASCADE_SPOTLIGHT.trailDecay;
    if (sample.strength < HERO_JOURNEY_CASCADE_SPOTLIGHT.trailCull) {
      trail.samples.splice(index, 1);
    }
  }
}
